import { createContext, useCallback, useContext, useEffect, useState } from "react";
import BrainDumpModal from "./BrainDumpModal";

/**
 * Global Brain Dump access.
 *
 * - Wraps the app with a BrainDumpProvider that owns the modal's open state.
 * - Exposes `useBrainDump()` → { open, close, isOpen, openBrainDump } to any component.
 * - Binds a global keyboard shortcut: Ctrl/Cmd + Shift + D opens the modal from anywhere.
 *   The listener ignores events when the user is typing in an input/textarea/
 *   contentEditable element so it never fights with what they're writing.
 */
const BrainDumpContext = createContext({
  openBrainDump: () => {},
  closeBrainDump: () => {},
  isBrainDumpOpen: false,
  brainDumpShipCount: 0,
});

export const useBrainDump = () => useContext(BrainDumpContext);

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
};

export function BrainDumpProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [shipCount, setShipCount] = useState(0);

  const openBrainDump = useCallback(() => setIsOpen(true), []);
  const closeBrainDump = useCallback(() => setIsOpen(false), []);
  const onShipped = useCallback(() => setShipCount((c) => c + 1), []);

  useEffect(() => {
    const handler = (e) => {
      // Accept Ctrl+Shift+D on Win/Linux and Cmd+Shift+D on macOS.
      const isShortcut =
        (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "D" || e.key === "d");
      if (!isShortcut) return;

      // Don't hijack the keystroke if the user is in a text field — unless
      // the modal is already open (then we actually want the shortcut to
      // close it even while they're mid-dump).
      if (!isOpen && isTypingTarget(document.activeElement)) return;

      e.preventDefault();
      setIsOpen((prev) => !prev);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  return (
    <BrainDumpContext.Provider
      value={{
        openBrainDump,
        closeBrainDump,
        isBrainDumpOpen: isOpen,
        brainDumpShipCount: shipCount,
      }}
    >
      {children}
      <BrainDumpModal open={isOpen} onClose={closeBrainDump} onShipped={onShipped} />
    </BrainDumpContext.Provider>
  );
}
